import { BORDER, GREY, BASE, BRAND, WARNING, ACTIONS, PRIMARY } from "../../utils/constants/colors";
import { borderRadius, fontSize, fontWeight, SHADOW } from "../../utils/constants/UI/uiConstants";

// Brand accents shared with the (deliberately custom) annotation editor + triggers.
// Sourced from the design-system tokens so they track any palette change.
export const BRAND_CORAL = BRAND.PRIMARY; // #FF4848
export const INK_DEEP = BRAND.SECONDARY; // #1E2939

// Z-order: floating button < report dialog < annotation editor.
export const Z_BUTTON = 1390;
export const Z_DIALOG = 1400;
export const Z_ANNOTATOR = 1500;

// Human-readable timeline line per event. Some events carry their detail in `meta`
// (status from/to, triage field changes, verify ok) rather than `body`, so a bare
// event_type would read as "not tracked". This formatter covers every type both the
// tenant and admin services write.
const EVENT_LABELS = {
  created: "Reported",
  status: "Triage Updated",
  triage: "Triage Updated",
  comment: "Comment",
  verify: "Reporter confirmation",
  plusone: "+1 (me too)",
  assist: "AI assist",
};

export const eventLabel = (event) => EVENT_LABELS[event?.event_type] || event?.event_type || "Event";

export const describeEvent = (event) => {
  const meta = event?.meta || {};
  switch (event?.event_type) {
    case "created":
      return `Report filed${meta.kind ? ` (${meta.kind})` : ""}${meta.screenshot_count ? `, ${meta.screenshot_count} screenshot(s)` : ""}`;
    case "status":
      return `status : ${meta.from || "?"} -> ${meta.to || "?"}${event.body ? `\n${event.body}` : ""}`;
    case "triage": {
      const changes = meta.changes || {};
      const parts = Object.keys(changes).map((field) => {
        const c = changes[field] || {};
        return `${field} : ${c.from ?? "-"} -> ${c.to ?? "-"}`;
      });
      return parts.length ? parts.join("\n") : "Fields updated";
    }
    case "verify":
      return meta.ok ? `Confirmed fixed${event.body ? `: ${event.body}` : ""}` : `Still broken / reopened${event.body ? `: ${event.body}` : ""}`;
    case "plusone":
      return "Marked +1 (me too)";
    case "assist":
      return event.body || "AI polish applied";
    case "comment":
    default:
      return event.body || "";
  }
};

// `variant` drives the design-system <StatusChip>; label stays the reporter-facing
// wording. Colors are no longer hand-rolled here - the chip owns its palette.
export const STATUS_META = {
  open: { label: "Open", variant: "blue" },
  triaged: { label: "Triaged", variant: "purple" },
  in_progress: { label: "In progress", variant: "orange" },
  fixed: { label: "Fixed - confirm?", variant: "green" },
  verified: { label: "Verified", variant: "green" },
  reopened: { label: "Reopened", variant: "red" },
  rejected: { label: "Rejected", variant: "navy" },
  duplicate: { label: "Duplicate", variant: "navy" },
  wont_fix: { label: "Won't fix", variant: "navy" },
};

export const styles = {
  // Floating button (white pill + ink bug icon/label, coral count badge)
  fab: {
    position: "fixed",
    right: "20px",
    bottom: "20px",
    zIndex: Z_BUTTON,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: BASE.WHITE,
    color: INK_DEEP,
    border: `1px solid ${BORDER.LIGHT}`,
    borderRadius: "24px",
    padding: "10px 18px",
    cursor: "pointer",
    boxShadow: SHADOW.md,
    fontFamily: "Inter, sans-serif",
    fontSize: fontSize.XS, // 12px token
    fontWeight: fontWeight.SEMIBOLD,
  },
  fabBadge: {
    position: "absolute",
    top: "-7px",
    right: "-5px",
    minWidth: "20px",
    height: "20px",
    borderRadius: "10px",
    background: BRAND_CORAL,
    color: BASE.WHITE,
    fontSize: fontSize.MICRO, // 10px token
    fontWeight: fontWeight.BOLD,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
  },

  // Sidebar-rail trigger: matches SidebarItem idiom (vertical icon + 12px label,
  // ink default / coral on hover, centered in the ~5rem rail). Lives at the drawer
  // bottom as a normal flow child - FIXED position, never moves. Raised z-index keeps
  // it clickable in that same spot even when a full-screen modal backdrop is open, so
  // it neither jumps nor gets blocked.
  railButton: {
    // Pinned to the sidebar rail's bottom-left corner (rail is 5rem wide). Fixed, so
    // it occupies one constant screen spot - never reflows, never overlaps right-side
    // panels, and stays above modal backdrops (Z_BUTTON > MUI backdrop 1300).
    position: "fixed",
    left: 0,
    bottom: 0,
    width: "5rem",
    zIndex: Z_BUTTON,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    boxSizing: "border-box",
    padding: "13px 8px",
    background: BASE.WHITE,
    border: "none",
    borderTop: `1px solid ${BORDER.LIGHT}`,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
  },
  railButtonActive: {
    background: PRIMARY.W_50, // #FFECEC - brand tint token
  },
  railLabel: {
    fontSize: fontSize.XS, // 12px token
    fontWeight: fontWeight.MEDIUM,
    whiteSpace: "nowrap",
  },
  railBadge: {
    position: "absolute",
    top: "6px",
    right: "10px",
    minWidth: "18px",
    height: "18px",
    borderRadius: "9px",
    background: BRAND_CORAL,
    color: BASE.WHITE,
    fontSize: fontSize.MICRO, // 10px token
    fontWeight: fontWeight.BOLD,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 4px",
  },

  // Dialog shell
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: Z_DIALOG,
    background: "rgba(30, 41, 57, 0.45)",
    display: "flex",
    // Pin to the TOP like the house CustomModal (which uses marginTop:64px), not
    // vertically centered - so the reporter lines up with every other app modal.
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "64px",
    paddingBottom: "24px",
    paddingLeft: "24px",
    paddingRight: "24px",
  },
  dialog: {
    background: BASE.WHITE,
    // House modals round 6px on the header's top corners and the footer's bottom
    // corners (CustomModal), NOT all four corners. The header/footer own their own
    // rounding, so the shell itself stays square and just clips the overflow.
    borderRadius: borderRadius.primary, // 6px token
    boxShadow: SHADOW.lg,
    width: "560px",
    maxWidth: "94vw",
    // 64px top offset + 24px bottom padding (set on the overlay) - keep the dialog
    // within that band so a tall report never runs off the bottom of the screen.
    maxHeight: "calc(100vh - 88px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "Inter, sans-serif",
    // Glide down from the top like every other modal instead of popping in. Keyframe
    // (qaSlideDown) lives in src/index.css next to the shared `spin` keyframe.
    animation: "qaSlideDown 260ms cubic-bezier(0.16, 1, 0.3, 1)",
  },
  // Matches the house CustomModal header exactly: dark grey bar, white title, 16px
  // padding, 6px rounding on the TOP corners only.
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    background: GREY.QUATINARY,
    borderTopLeftRadius: borderRadius.primary,
    borderTopRightRadius: borderRadius.primary,
  },
  // Close (cross) button: matches the house CustomModal idiom - icon wrapped in a
  // container, white glyph on the dark header, hover background. The color MUST be
  // set via this style object (not the IconComponent `color` prop) because the prop
  // is overridden by any `style` spread onto the underlying MUI <Icon>.
  closeIconContainer: { maxHeight: "24px" },
  closeIconStyle: {
    cursor: "pointer",
    fontSize: `${fontSize.XL} !important`,
    color: BASE.WHITE,
  },
  dialogBody: { padding: "16px", overflowY: "auto", flex: 1 },
  // Matches the house CustomModal footer: 16px padding, ACTIONS.DEFAULT top border,
  // white background, 6px rounding on the BOTTOM corners only.
  dialogFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px",
    background: BASE.WHITE,
    borderTop: `1px solid ${ACTIONS.DEFAULT}`,
    borderBottomLeftRadius: borderRadius.primary,
    borderBottomRightRadius: borderRadius.primary,
    flexWrap: "wrap",
  },

  // Layout helpers shared by the steps. A field block keeps the old label->control
  // spacing now that labels are <TypographyInline> instead of styled <label>s.
  fieldBlock: { marginTop: "12px" },
  // Wrap an inline DS <Button> (width:100% by default) so it sizes to its label.
  inlineBtn: { maxWidth: "fit-content" },

  kindToggle: { display: "flex", gap: "8px" },

  shotsStrip: { display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" },
  shotThumbWrap: { position: "relative", width: "96px", height: "64px", borderRadius: borderRadius.primary, overflow: "hidden", border: `1px solid ${BORDER.DEFAULT}`, cursor: "pointer" },
  shotThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  shotSkeleton: {
    width: "96px", height: "64px", borderRadius: borderRadius.primary, border: `1px solid ${BORDER.DEFAULT}`,
    background: ACTIONS.HOVER, display: "flex", alignItems: "center", justifyContent: "center",
  },
  shotSpinner: {
    width: "18px", height: "18px", borderRadius: "50%",
    border: `2px solid ${BORDER.DEFAULT}`, borderTopColor: BRAND_CORAL,
    display: "inline-block", animation: "spin 0.8s linear infinite",
  },
  shotRemove: {
    position: "absolute", top: "2px", right: "2px", width: "18px", height: "18px",
    // Circular scrim button - rgba of BRAND.SECONDARY (#1E2939) keeps the thumb readable underneath.
    borderRadius: "9px", border: "none", background: "rgba(30,41,57,0.75)", color: BASE.WHITE,
    fontSize: fontSize.MICRO, lineHeight: "18px", textAlign: "center", cursor: "pointer", padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  shotAnnotateHint: {
    position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(30,41,57,0.65)",
    color: BASE.WHITE, fontSize: fontSize.MICRO, textAlign: "center", padding: "2px 0",
  },

  dropHint: (highlight) => ({
    marginTop: "10px",
    border: `1.5px dashed ${highlight ? BRAND_CORAL : BORDER.DEFAULT}`,
    background: highlight ? PRIMARY.W_50 : ACTIONS.HOVER, // brand tint on drag-over, neutral idle
    borderRadius: borderRadius.primary, // 6px token
    padding: "12px",
    textAlign: "center",
    fontSize: fontSize.XS, // 12px token
    color: GREY.QUATINARY,
  }),

  listRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "10px 4px",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    cursor: "pointer",
  },

  banner: {
    background: WARNING.W_50,
    border: `1px solid ${WARNING.W_500}`,
    borderRadius: borderRadius.primary, // 6px token
    padding: "10px 12px",
    fontSize: fontSize.XS, // 12px token
    color: GREY.PRIMARY,
    marginTop: "12px",
  },

  // Timeline: a dot per event with a thin connector running between dots. Replaces the
  // old `borderLeft` rail, which read as a decorative colored left-border accent (a
  // design-system user-rule violation). The connector is drawn on the marker column,
  // never as a left-border on the content.
  timeline: { marginTop: "8px", padding: 0, margin: "8px 0 0 0", listStyle: "none" },
  // Each row: [marker column | content]. The marker column hosts the dot and the
  // connector line that links one event to the next.
  timelineItem: {
    display: "flex",
    gap: "10px",
    fontSize: fontSize.XS, // 12px token
    color: GREY.QUATINARY,
  },
  timelineMarker: {
    position: "relative",
    flex: "0 0 auto",
    width: "10px",
    display: "flex",
    justifyContent: "center",
  },
  timelineDot: {
    width: "8px",
    height: "8px",
    marginTop: "4px",
    borderRadius: "50%",
    background: BRAND_CORAL,
    flex: "0 0 auto",
    zIndex: 1,
  },
  // Connector grows to fill the row height between this dot and the next.
  timelineConnector: {
    position: "absolute",
    top: "10px",
    bottom: "-4px",
    left: "50%",
    width: "2px",
    marginLeft: "-1px",
    background: BORDER.DEFAULT,
  },
  timelineContent: { flex: 1, paddingBottom: "10px" },
};
